import { Avatar, Colors, Icon } from '@amelio/ui-library';

import type { EmployeeInfo } from '../types';

import { useAmelioTranslations } from '@/hooks';
import { getInitials, getProfilePictureUrl } from '@/utils/user';

interface EmployeeInfoCardProps {
    employee: EmployeeInfo;
    dataTestId?: string;
}

export const EmployeeInfoCard = ({
    employee,
    dataTestId = 'employee-info-card',
}: EmployeeInfoCardProps) => {
    const { t } = useAmelioTranslations();
    const fullName = `${employee.firstName} ${employee.lastName}`;

    return (
        <div
            className={'relative w-full max-w-[399px]'}
            data-testid={dataTestId}
        >
            <div
                className={
                    'bg-primary absolute -top-3 right-0 z-10 inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs font-medium text-white'
                }
            >
                {t('managerAssessmentEvaluation.employeeCard.selected')}
            </div>

            <div
                className={
                    'border-primary mt-3 flex items-center justify-center rounded-xl border bg-white/45 p-6 backdrop-blur-[2px] md:p-6'
                }
            >
                {/* No minimum width: pinning the inner card at 270px left 81px of the 351px the
                    outer card offers unused, and squeezed the text column to 82.5px — just under
                    what a role title needs. */}
                <div
                    className={
                        'w-full max-w-[320px] rounded-2xl border border-white bg-white/80 p-3 backdrop-blur-[2px] md:p-4'
                    }
                >
                    <div className={'flex items-center gap-3 p-1.5 md:gap-5'}>
                        <Avatar
                            src={getProfilePictureUrl(employee.avatarUrl) || undefined}
                            alt={fullName}
                            initials={getInitials(employee)}
                            bgColor={'purple'}
                            size={'sm'}
                        />

                        <div className={'flex min-w-0 flex-1 items-center gap-3 md:gap-6'}>
                            <div className={'flex min-w-0 flex-1 flex-col gap-0.5'}>
                                <span
                                    className={
                                        'truncate text-[14px] leading-[18px] font-medium text-gray-900'
                                    }
                                    title={fullName}
                                >
                                    {fullName}
                                </span>
                                {employee.roleTitle ? (
                                    <span
                                        className={
                                            'truncate text-[10px] leading-normal text-gray-500 md:text-[11px]'
                                        }
                                        title={employee.roleTitle}
                                    >
                                        {employee.roleTitle}
                                    </span>
                                ) : null}
                            </div>

                            <div
                                className={
                                    'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-purple-100'
                                }
                            >
                                <Icon
                                    name={'school-briefcase'}
                                    size={'lg'}
                                    color={Colors.SECONDARY}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
